<template>
  <div class="layout-wrapper">
    <div v-if="xMiddle.error">
      <h4>Sorry!<br>Leider hat das Laden dieser Visualisierung nicht geklappt.
        Bitte versuche es erneut oder kontaktiere
        das Trendence-Team!
      </h4>
    </div>
    <div class="dashboard" :style="`background-color: ${settings.gridItemColor || '#F7F7F7'};`">
      <GridLayout :preventCollision="preventCollision" :margin="[0, 0]" :rowHeight="settings.rowHeight"
        v-model:layout="grid" :verticalCompact="verticalCompact" :colNum="settings.colNum" :isDraggable="isDraggable"
        :isResizable="isResizable" :autoSize="autoSize" :isBounded="isBounded">
        <GridItem v-for="(item, index) in grid" :static="false" :x="item.x" :y="item.y" :w="item.w" :h="item.h"
          :i="item.i" :class="`${item.isCard ? 'rounded-[20px]' : ''}`" :style="`background-color:${item.cardColor ?? '#FFFFFF'};
              color: ${item.fontColor ?? '#000000'};
              ${GridAddedComps[index]?.type == 'table' ?
              'overflow: auto; border-bottom: solid 1px #e5e7eb; padding: 5px;' : ''}
              ${item.isCard ? 'box-shadow: 5px 5px 20px #00070F1A;' : ''}
              ${item?.expandItem ? 'display: none;' : ''}`" :key="`item_${item.i}`">
          <div class="widget-container">
            <div v-if="hasComponentError(GridAddedComps[index])" class="error-reload-container">
              <button @click="reloadPage" class="reload-button">
                <RefreshIcon class="reload-icon" />
                <span class="reload-text">Komponente konnte nicht geladen werden. Bitte lade die Seite noch einmal
                  neu</span>
              </button>
            </div>
            <component v-else :is="`widget-${GridAddedComps[index]?.isGroup ? 'group' : GridAddedComps[index]?.type}`"
              v-model="GridAddedComps[index]" :components="xMiddle.components" :layout="GridAddedComps"
              :isCard="GridAddedComps[index]?.isCard ?? false" :isGroup="GridAddedComps[index]?.isGroup ?? false"
              :groupId="GridAddedComps[index]?.groupId ?? ''" :isBig="GridAddedComps[index]?.isBig ?? false" />
          </div>
        </GridItem>
      </GridLayout>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, defineAsyncComponent, shallowRef, computed, toRaw, onMounted } from "vue"
import { GridLayout, GridItem } from 'grid-layout-plus'
import { storeToRefs } from "pinia"
import { useProductBaseStore } from "@xlayer/store/productXBaseStore"

const store = useProductBaseStore();
const { xMiddle, productSetup } = storeToRefs(store);
const { addXMiddleData } = store;
const isBounded = ref(true)
const verticalCompact = ref(false)
const preventCollision = ref(true)
const autoSize = ref(true)
const isDraggable = ref(false)
const isResizable = ref(false)

addXMiddleData()

const layout = shallowRef(productSetup.value.layout)
const settings = ref(layout.value.settings)
const grid = ref(layout.value.grid)

const GridAddedComps = computed(() => {
  const components = xMiddle.value.components;
  const currentGrid = grid.value;

  return currentGrid.map((gridItem: any) => {
    const component = components.find(({ id }: { id: string }) => id === gridItem.componentId);
    return {
      ...component,
      ...gridItem
    };
  });
});

const hasComponentError = (component: any): boolean => {
  if ((!component || !component.id) && !component?.isGroup && !component?.type) return true;
  if (component.error) return true;
  return false;
};

const reloadPage = (): void => {
  window.location.reload();
};

onMounted(async () => {
  grid.value = structuredClone(toRaw(grid.value));
});
</script>

<script lang="ts">
const ChartWidget = defineAsyncComponent(() => import('@xlayer/widgets/ChartWidgetDefault.vue'))
const MapWidget = defineAsyncComponent(() => import('@xlayer/widgets/MapWidgetDefault.vue'))
const FilterWidget = defineAsyncComponent(() => import('@xlayer/widgets/FilterWidgetDefault.vue'))
const TableWidget = defineAsyncComponent(() => import('@xlayer/widgets/TableWidgetDefault.vue'))
const ScoreWidget = defineAsyncComponent(() => import('@xlayer/widgets/ScoreWidgetDefault.vue'))
const TagsWidget = defineAsyncComponent(() => import('@xlayer/widgets/TagsWidgetDefault.vue'))
const HeadingWidget = defineAsyncComponent(() => import('@xlayer/widgets/HeadingWidgetDefault.vue'))
const VacanciesWidget = defineAsyncComponent(() => import('@xlayer/widgets/VacanciesWidgetDefault.vue'))
const DeltaWidget = defineAsyncComponent(() => import('@xlayer/widgets/DeltaWidgetDefault.vue'))
const TextWidget = defineAsyncComponent(() => import('@xlayer/widgets/TextWidgetDefault.vue'))
const GroupWidget = defineAsyncComponent(() => import('@xlayer/widgets/GroupWidgetDefault.vue'))
const RefreshIcon = defineAsyncComponent(() => import('@images/icons/refresh.svg'))

export default {
  components: {
    'widget-filter': FilterWidget,
    'widget-chart': ChartWidget,
    'widget-map': MapWidget,
    'widget-table': TableWidget,
    'widget-score': ScoreWidget,
    'widget-tags': TagsWidget,
    'widget-heading': HeadingWidget,
    'widget-vacancies': VacanciesWidget,
    'widget-delta': DeltaWidget,
    'widget-text': TextWidget,
    'widget-group': GroupWidget,
    RefreshIcon
  }
}
</script>

<style lang="sass">
.widget-container
  height: 100%
  width: 100%
.dashboard
  h1
    font-size: 40px
  h2
    color: inherit
.handout-buttons
  display: flex
  justify-content: end
.btn.disabled
  pointer-events: none
  opacity: 0.6
</style>

<style lang="sass" scoped>
h1
  font-size: 40px
.el-main
  height: 100%
.layout-wrapper.el-loading-parent--relative
  z-index: 1

.error-reload-container
  display: flex
  align-items: center
  justify-content: center
  height: 100%
  width: 100%

.reload-button
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  gap: 12px
  padding: 20px 32px
  background-color: transparent
  border: none
  cursor: pointer
  transition: all 0.2s ease
  color: #374151
  font-size: 14px
  font-weight: 500

  &:hover
    color: #006adf

  &:active
    transform: scale(0.98)

.reload-icon
  width: 24px
  height: 24px
  color: inherit

.reload-text
  color: inherit
</style>
